import React, { useEffect, useMemo, useState } from "react";
import { api, fmt } from "../../../services/erpApi";
import { StatCard, PageHeader, LoadingScreen, EmptyState } from "../../../components/admin/EssUi";
import { BookOpen, Banknote } from "lucide-react";

const AdminPanelLedgerPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [journals, setJournals] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("ledger");

  const [accountId, setAccountId] = useState("");
  const [journalId, setJournalId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [state, setState] = useState("posted");

  const [glResult, setGlResult] = useState(null);
  const [glLoading, setGlLoading] = useState(false);
  const [tbResult, setTbResult] = useState(null);
  const [tbLoading, setTbLoading] = useState(false);

  useEffect(() => {
    (async () => {
      await Promise.all([
        api.accounts.list({ limit: 500 }).catch(() => []),
        api.journals.list({ limit: 500 }).catch(() => []),
      ]).then(([a, j]) => {
        setAccounts(a || []);
        setJournals(j || []);
      });
      setLoaded(true);
    })();
  }, []);

  const buildParams = (extra = {}) => {
    const p = {};
    if (accountId) p.account_id = parseInt(accountId);
    if (accountId === "all") { p.account_from = "0"; p.account_to = "999999"; }
    if (journalId) p.journal_id = parseInt(journalId);
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return { ...p, ...extra };
  };

  const runGl = async () => {
    setGlLoading(true);
    setGlResult(null);
    try {
      const res = await api.accounting.generalLedger(buildParams());
      setGlResult(res?.data);
    } catch (e) {
      setGlResult({ error: e.message });
    }
    setGlLoading(false);
  };

  const runTb = async () => {
    setTbLoading(true);
    setTbResult(null);
    try {
      const res = await api.accounting.trialBalance({ date_from: dateFrom || undefined, date_to: dateTo || undefined, state: state || undefined });
      setTbResult(Array.isArray(res) ? res : res?.data || []);
    } catch (e) {
      setTbResult([]);
    }
    setTbLoading(false);
  };

  const glRows = useMemo(() => {
    if (!glResult || Array.isArray(glResult)) return glResult || [];
    return null; // single object
  }, [glResult]);

  const tbTotals = useMemo(() => {
    if (!tbResult) return null;
    let debit = 0, credit = 0;
    tbResult.forEach((r) => { debit += Number(r.debit) || 0; credit += Number(r.credit) || 0; });
    return { debit, credit };
  }, [tbResult]);

  const inputStyle = {
    width: "100%", padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px",
    fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box",
  };

  if (!loaded) return <LoadingScreen />;

  const singleAccount = glResult && !Array.isArray(glResult) && !glResult?.error;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader title="General Ledger" subtitle="Journal entries per account and trial balance" />

      <div style={{ display: "flex", gap: "24px", marginBottom: "20px", borderBottom: "1px solid #e2e7e2", overflowX: "auto" }}>
        {[
          { key: "gl", label: "General Ledger" },
          { key: "tb", label: "Trial Balance" },
        ].map((t) => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{ padding: "9px 2px", border: "none", background: "transparent", borderBottom: `2px solid ${activeTab === t.key ? "#225f2b" : "transparent"}`, marginBottom: -1, whiteSpace: "nowrap", color: activeTab === t.key ? "#225f2b" : "#64748b", fontSize: "13px", fontWeight: activeTab === t.key ? "600" : "500", cursor: "pointer" }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "24px", background: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e7e2" }}>
        <div style={{ minWidth: "200px", flex: 1 }}>
          <label style={{ fontSize: "11px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>Account</label>
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
            <option value="">Select account…</option>
            <option value="all">All accounts (range)</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
          </select>
        </div>
        <div style={{ minWidth: "160px" }}>
          <label style={{ fontSize: "11px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>Journal</label>
          <select value={journalId} onChange={(e) => setJournalId(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
            <option value="">All journals</option>
            {journals.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
          </select>
        </div>
        <div style={{ minWidth: "150px" }}>
          <label style={{ fontSize: "11px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ minWidth: "150px" }}>
          <label style={{ fontSize: "11px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={inputStyle} />
        </div>
        {activeTab === "tb" && (
          <div style={{ minWidth: "140px" }}>
            <label style={{ fontSize: "11px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "4px" }}>State</label>
            <select value={state} onChange={(e) => setState(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
              <option value="posted">Posted</option>
              <option value="draft">Draft</option>
              <option value="">All</option>
            </select>
          </div>
        )}
        <div style={{ display: "flex", alignItems: "flex-end" }}>
          <button onClick={activeTab === "gl" ? runGl : runTb} className="ess-btn ess-btn-primary">
            Run Report
          </button>
        </div>
      </div>

      {activeTab === "gl" ? (
        glLoading ? (
          <LoadingScreen />
        ) : glResult?.error ? (
          <EmptyState icon="⚠️" title="Query error" subtitle={glResult.error} />
        ) : singleAccount ? (
          <div style={{ display: "grid", gap: "16px", marginBottom: "24px" }} className="ess-rg-2">
            <StatCard label={glResult.account_name || "Account"} value={glResult.account_code || "—"} icon={BookOpen} trend={`Opening: ${fmt.money(glResult.opening_balance)}`} />
            <StatCard label="Balance" value={fmt.moneyShort(glResult.cumulative_balance)} icon={Banknote} trend={fmt.money(glResult.cumulative_balance)} />
          </div>
        ) : null
      ) : null}

      {activeTab === "gl" && !glLoading && singleAccount && (
        <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
          <div style={{ padding: "14px 16px", background: "#fafbfa", borderBottom: "1px solid #e2e7e2", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "14px", fontWeight: "600", color: "#1e293b" }}>
              {glResult.account_code} — {glResult.account_name} <span style={{ color: "#64748b", fontWeight: "400" }}>· Opening {fmt.money(glResult.opening_balance)}</span>
            </span>
            <span style={{ fontSize: "13px", fontWeight: "700", color: "#225f2b" }}>Balance {(glResult.cumulative_balance ?? 0) < 0 ? "-" : ""}{fmt.money(Math.abs(glResult.cumulative_balance ?? 0))}</span>
          </div>
          {!glResult.entries?.length ? (
            <EmptyState icon="📭" title="No entries" subtitle="No journal entries for this account on the selected period" />
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                <thead>
                  <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                    <th className="ess-th" style={{ minWidth: "100px" }}>Date</th>
                    <th className="ess-th" style={{ minWidth: "90px" }}>Journal</th>
                    <th className="ess-th" style={{ minWidth: "80px" }}>Ref</th>
                    <th className="ess-th" style={{ minWidth: "160px" }}>Label</th>
                    <th className="ess-th" style={{ minWidth: "120px" }}>Partner</th>
                    <th className="ess-th ess-th-r" style={{ minWidth: "100px" }}>Debit</th>
                    <th className="ess-th ess-th-r" style={{ minWidth: "100px" }}>Credit</th>
                    <th className="ess-th ess-th-r" style={{ minWidth: "100px" }}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {glResult.entries.map((e, index) => (
                    <tr key={index} className="ess-tr" style={{ borderBottom: index < glResult.entries.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                      <td className="ess-td" style={{ color: "#475569", whiteSpace: "nowrap" }}>{fmt.date(e.date)}</td>
                      <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{e.journal || "—"}</td>
                      <td className="ess-td" style={{ fontSize: "12px", color: "#94a3b8", fontFamily: "monospace", whiteSpace: "nowrap" }}>{e.ref || "—"}</td>
                      <td className="ess-td" style={{ fontWeight: "500", color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.label || "—"}</td>
                      <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.partner || "—"}</td>
                      <td className="ess-td" style={{ color: "#225f2b", textAlign: "right", whiteSpace: "nowrap" }}>{e.debit ? fmt.money(e.debit) : "—"}</td>
                      <td className="ess-td" style={{ color: "#be123c", textAlign: "right", whiteSpace: "nowrap" }}>{e.credit ? fmt.money(e.credit) : "—"}</td>
                      <td className="ess-td" style={{ fontWeight: "700", color: (e.balance ?? 0) < 0 ? "#be123c" : "#1e293b", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(e.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "gl" && !glLoading && !glResult && (
        <EmptyState icon="📘" title="No data" subtitle="Configure filters above and run the report" />
      )}

      {activeTab === "tb" && (
        tbLoading ? (
          <LoadingScreen />
        ) : tbResult.length === 0 ? (
          <EmptyState icon="📊" title="No data" subtitle="Configure filters above and run the trial balance" />
        ) : (
          <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                <thead>
                  <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                    <th className="ess-th" style={{ minWidth: "110px" }}>Code</th>
                    <th className="ess-th" style={{ minWidth: "220px" }}>Account</th>
                    <th className="ess-th ess-th-r" style={{ minWidth: "120px" }}>Debit</th>
                    <th className="ess-th ess-th-r" style={{ minWidth: "120px" }}>Credit</th>
                    <th className="ess-th ess-th-r" style={{ minWidth: "130px" }}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {tbResult.map((r, index) => (
                    <tr key={index} className="ess-tr" style={{ borderBottom: index < tbResult.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                      <td className="ess-td" style={{ fontWeight: "600", color: "#225f2b", fontFamily: "monospace", whiteSpace: "nowrap" }}>{r.account_code || "—"}</td>
                      <td className="ess-td" style={{ fontWeight: "500", color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.account_name || "—"}</td>
                      <td className="ess-td" style={{ color: "#225f2b", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(r.debit)}</td>
                      <td className="ess-td" style={{ color: "#be123c", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(r.credit)}</td>
                      <td className="ess-td" style={{ fontWeight: "700", color: (r.balance ?? 0) < 0 ? "#be123c" : "#1e293b", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(r.balance)}</td>
                    </tr>
                  ))}
                  {tbTotals && tbTotals.debit !== 0 && (
                    <tr style={{ background: "#fafbfa", borderTop: "1px solid #e2e7e2" }}>
                      <td className="ess-td" style={{ fontWeight: "700", color: "#1e293b" }} colSpan={2}>Total</td>
                      <td className="ess-td" style={{ fontWeight: "700", color: "#225f2b", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(tbTotals.debit)}</td>
                      <td className="ess-td" style={{ fontWeight: "700", color: "#be123c", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(tbTotals.credit)}</td>
                      <td className="ess-td" style={{ fontWeight: "700", color: "#225f2b", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(tbTotals.debit - tbTotals.credit)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {activeTab === "gl" && !glLoading && Array.isArray(glResult) && glResult.length > 0 && (
        <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr style={{ background: "#fafbfa", borderBottom: "1px solid #e2e7e2" }}>
                  <th className="ess-th" style={{ minWidth: "110px" }}><BookOpen size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />Code</th>
                  <th className="ess-th" style={{ minWidth: "220px" }}>Account</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "110px" }}>Opening</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "110px" }}>Debit</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "110px" }}>Credit</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "130px" }}>Closing</th>
                </tr>
              </thead>
              <tbody>
                {glRows.map((r, index) => {
                  const debit = (r.entries || []).reduce((s, e) => s + (Number(e.debit) || 0), 0);
                  const credit = (r.entries || []).reduce((s, e) => s + (Number(e.credit) || 0), 0);
                  return (
                    <tr key={index} className="ess-tr" style={{ borderBottom: index < glRows.length - 1 ? "1px solid #f1f3f0" : "none" }}>
                      <td className="ess-td" style={{ fontWeight: "600", color: "#225f2b", fontFamily: "monospace", whiteSpace: "nowrap" }}>{r.account_code || "—"}</td>
                      <td className="ess-td" style={{ fontWeight: "500", color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.account_name || "—"}</td>
                      <td className="ess-td" style={{ color: "#64748b", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(r.opening_balance)}</td>
                      <td className="ess-td" style={{ color: "#225f2b", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(debit)}</td>
                      <td className="ess-td" style={{ color: "#be123c", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(credit)}</td>
                      <td className="ess-td" style={{ fontWeight: "700", color: (r.cumulative_balance ?? 0) < 0 ? "#be123c" : "#1e293b", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(r.cumulative_balance)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "gl" && glResult?.error && (
        <EmptyState icon="⚠️" title="Query error" subtitle={glResult.error} />
      )}
    </div>
  );
};

export default AdminPanelLedgerPage;